import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ONBOARDING_KEY = "uc_oauth_onboarded";

const OAuthOnboarding = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({
    order_updates: true,
    marketing: false,
    restock_notifications: false,
    sms_notifications: false,
  });

  useEffect(() => {
    if (!user) return;

    // Only trigger for OAuth users (have a provider other than email as primary)
    const providers = user.app_metadata?.providers as string[] | undefined;
    const isOAuth = providers && providers.some((p) => p !== "email");
    if (!isOAuth) return;

    // Check if already onboarded
    const onboarded = localStorage.getItem(ONBOARDING_KEY);
    if (onboarded) return;

    // Check if they already have email preferences set (returning user)
    supabase
      .from("email_preferences")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          // Already has preferences, mark as onboarded
          localStorage.setItem(ONBOARDING_KEY, "true");
        } else {
          // First OAuth login - show onboarding
          setOpen(true);
        }
      });
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    const { error } = await supabase.from("email_preferences").upsert(
      {
        user_id: user.id,
        ...prefs,
      },
      { onConflict: "user_id" }
    );

    if (error) {
      toast.error("Failed to save preferences");
      return;
    }

    localStorage.setItem(ONBOARDING_KEY, "true");
    setOpen(false);
    toast.success("Preferences saved!");
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Welcome to Union Collection</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Choose which emails you'd like to receive from us.
        </p>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="order-updates" className="text-sm">Order updates</Label>
            <Switch
              id="order-updates"
              checked={prefs.order_updates}
              onCheckedChange={(v) => setPrefs((p) => ({ ...p, order_updates: v }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="marketing" className="text-sm">New drops & promotions</Label>
            <Switch
              id="marketing"
              checked={prefs.marketing}
              onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="restock" className="text-sm">Restock notifications</Label>
            <Switch
              id="restock"
              checked={prefs.restock_notifications}
              onCheckedChange={(v) => setPrefs((p) => ({ ...p, restock_notifications: v }))}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={handleSkip}>
            Skip
          </Button>
          <Button size="sm" onClick={handleSave}>
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OAuthOnboarding;
